import type { LineEnding, TextFormat } from '../core/documents';

/**
 * Byte ⇄ text conversion for ordinary text files.
 *
 * The editor works with `\n`-joined text. A file's original line ending and
 * UTF-8 byte-order mark are remembered and restored on save, so a file that
 * merely passes through MyTXT keeps its shape.
 */

export class NotTextError extends Error {
  constructor() {
    super('This file does not look like text.');
  }
}

export interface DecodedText {
  text: string;
  format: TextFormat;
  /** Set when the bytes were not valid UTF-8 and a legacy decoding was used. */
  legacyEncoding?: string;
}

const BOM = [0xef, 0xbb, 0xbf];

export function decodeText(bytes: Uint8Array): DecodedText {
  const sample = bytes.subarray(0, 8192);
  if (sample.includes(0)) throw new NotTextError();

  const bom = bytes.length >= 3 && BOM.every((b, i) => bytes[i] === b);
  const body = bom ? bytes.subarray(3) : bytes;
  let raw: string;
  let legacyEncoding: string | undefined;
  try {
    raw = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(body);
  } catch {
    raw = new TextDecoder('windows-1252').decode(body);
    legacyEncoding = 'Windows-1252';
  }
  const eol = detectLineEnding(raw);
  const text = raw.replace(/\r\n?/g, '\n');
  return { text, format: { eol, bom }, legacyEncoding };
}

/** The dominant line ending; ties and line-less files default to `\n`. */
export function detectLineEnding(raw: string): LineEnding {
  const crlf = raw.match(/\r\n/g)?.length ?? 0;
  const lf = (raw.match(/\n/g)?.length ?? 0) - crlf;
  return crlf > lf ? '\r\n' : '\n';
}

export function encodeText(text: string, format: TextFormat): Uint8Array<ArrayBuffer> {
  const joined = format.eol === '\r\n' ? text.replace(/\n/g, '\r\n') : text;
  const body = new TextEncoder().encode(joined);
  if (!format.bom) return body;
  const out = new Uint8Array(body.length + 3);
  out.set(BOM, 0);
  out.set(body, 3);
  return out;
}
